import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { BarStack, Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import _ from 'lodash';

import {
  getParams,
  getMonth,
  thousandFormat,
  thousandFormat2,
  getMonths,
  financialMonth
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

// accessors
const getXValue = (d) => d.month;

let tooltipTimeout;

const TopChart = ({
  summaryData,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  defaultStartMonth,
  defaultMonth,
  defaultYear,
  handleFilter,
}) => {
  const canvasRef = useRef(null);
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [colors, setColors] = useState([]);
  const [xTransform, setXTransform] = useState(0);
  const [X, setX] = useState(0);
  const [iX, setIX] = useState(0);
  const [iY, setIY] = useState(0);
  const [rX, setRX] = useState(0);
  const [rY, setRY] = useState(0);
  const [rW, setRW] = useState(0);
  const [rH, setRH] = useState(0);
  const [canvasIndex, setCanvasIndex] = useState(-1);
  const [dragFlag, setDragFlag] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let dictBudgets = {};
    let dataArray = [];
    let totalsArray = [];
    let budgetsArray = [];

    let colorsArray = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    let fiscalMonths = getMonths(defaultStartMonth);
    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['value'] = {};
      dictData[month]['future'] = false;
      selectedYears.forEach((year) => {
        dictData[month][year] = 0;
      });

      dictTotals[month] = 0;
      dictBudgets[month] = 0;
    });

    let _summaryData = _.map(
      _.groupBy(summaryData, 'Date'),
      function (vals, Date) {
        return _.reduce(
          vals,
          function (m, o) {
            for (var p in o)
              if (p !== 'Date') {
                if (
                  p === 'ActualFees' ||
                  p === 'BudgetFees' ||
                  p === 'ForecastFees'
                )
                  m[p] = (parseFloat(m[p]) || 0) + parseFloat(o[p]);
                else m[p] = o[p];
              }

            return m;
          },
          { Date: Date },
        );
      },
    );

    _summaryData.forEach((item) => {
      const storedMonth = getMonth(item.Date);
      // Summary dates already come back as real calendar months.
      // dictData is keyed by 0-based calendar month index and then ordered
      // into fiscal order via getMonths(defaultStartMonth), so do not remap
      // the month here or July ends up in the January slot, etc.
      const month = storedMonth;
      if (dictData[month] !== undefined) {
        if (
          item.DimDateKey >
            (selectedYears[0] - 1) * 10000 +
              (defaultStartMonth - 1) * 100 +
              1 &&
          item.DimDateKey <=
            selectedYears[0] * 10000 +
              (defaultStartMonth - 1) * 100 +
              1
        ) {
          if (!item.ActualFlag) {
            dictData[month][item.FY] = parseFloat(item.ForecastFees);
            dictData[month]['future'] = true;
          } else {
            dictData[month][item.FY] = parseFloat(item.ActualFees);
            dictData[month]['future'] = false;
          }
        }
        dictData[month]['value'][item.FY] = item;
        dictData[month]['BudgetFees'] = parseFloat(item.BudgetFees);
        dictTotals[month] += dictData[month][item.FY];
        dictBudgets[month] += parseFloat(item.BudgetFees);
      }
    });

    fiscalMonths.forEach((month) => {
      dataArray.push(dictData[month]);
      totalsArray.push(dictTotals[month]);
      budgetsArray.push(dictBudgets[month]);
    });

    dataArray.map((d) => {
      if (
        d.future === true &&
        d[selectedYears[0]] === 0 &&
        Object.keys(d.value).length > 0
      ) {
        d[selectedYears[0]] = d.value[selectedYears[0]].BudgetFees;
        d.value[selectedYears[0]].ForecastFees =
          d.value[selectedYears[0]].BudgetFees;
      }
      return d;
    });

    let xTransformValue = 0;
    let maxYValue = Math.max(
      Math.abs(Math.min(Math.min(...totalsArray), Math.min(...budgetsArray))),
      Math.max(Math.max(...totalsArray), Math.max(...budgetsArray)),
    );
    if (
      !isFinite(maxYValue) ||
      maxYValue === Number.NEGATIVE_INFINITY ||
      isNaN(maxYValue) ||
      maxYValue === 0
    )
      xTransformValue = 30;
    else {
      xTransformValue = 9.5 * thousandFormat2(maxYValue).length;
    }

    setData(dataArray);
    setTotals(totalsArray);
    setBudgets(budgetsArray);
    setColors(colorsArray);
    setXTransform(xTransformValue);
  }, [summaryData, selectedYears, defaultStartMonth]);

  const getColor = useCallback((bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;

    const actual = bar.bar.data.value[year]
      ? bar.bar.data.future
        ? bar.bar.data.value[year].ForecastFees
        : bar.bar.data.value[year].ActualFees
      : 0;
    const budget = bar.bar.data.value[year]
      ? bar.bar.data.value[year].BudgetFees
      : 0;

    const activeColor =
      actual < budget ? negativeActiveColor : positiveActiveColor;
    const disableColor =
      actual < budget ? negativeDisableColor : positiveDisableColor;

    if (selectedTopItems.length === 0 && selectedMonths.length === 0) {
      return activeColor;
    }

    if (selectedTopItems.length === 0)
      if (selectedMonths.indexOf(month) > -1) return activeColor;

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (
        selectedTopItems[i].month === month &&
        selectedTopItems[i].year === year
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedMonths, selectedTopItems]);

  const getMousePositionOnCanvas = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const drawRectangle = (end = false) => {
    const context = canvasRef.current.getContext('2d');
    context.beginPath();
    context.lineWidth = '1';
    context.strokeStyle = '#00f3ff';
    context.rect(rX, rY, rW, rH);
    context.stroke();
    context.globalAlpha = 0.2;
    context.fillStyle = 'lightblue';
    context.fill();
    if (end) {
      drawSelectBar();
    }
  };

  const resetCanvas = () => {
    setX(0);
    setIX(0);
    setIY(0);
    setRX(0);
    setRY(0);
    setRW(0);
    setRH(0);
    setCanvasIndex(-1);
    setDragFlag(false);
    setIsMouseDown(false);
    
    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    context.beginPath();
  };

  const drawSelectBar = () => {
    let unitWidth = (window.innerWidth - 16 - xTransform) / 12;
    let startPosition = X >= iX ? iX - xTransform : X - xTransform;
    let endPosition = X >= iX ? X - xTransform : iX - xTransform;
    let startMonth = Math.floor(startPosition / unitWidth);
    let endMonth = Math.floor(endPosition / unitWidth);
    let _selectedTopItems = [],
      _selectedMonths = [];

    for (let i = startMonth; i <= endMonth; i++) {
      let _month = financialMonth(i, defaultStartMonth);
      _month = _month === 12 ? 0 : _month;
      if (i + 1 < defaultStartMonth)
        _selectedTopItems.push({ month: _month, year: selectedYears[0] - 1 });
      else _selectedTopItems.push({ month: _month, year: selectedYears[0] });
      _selectedMonths.push(_month);
    }

    if (rX === 0 && rY === 0 && _selectedTopItems.length > 0) {
      if (
        _.isEqual(
          selectedTopItems[0],
          _selectedTopItems[_selectedTopItems.length - 1],
        )
      ) {
        if (selectedTopItems.length === _selectedTopItems.length)
          deSelectAll();
        else
          handleFilter({
            selectedTopItems: [_selectedTopItems[_selectedTopItems.length - 1]],
            selectedMonths: [_selectedMonths[_selectedTopItems.length - 1]],
          });
      } else
        handleFilter({
          selectedTopItems: [_selectedTopItems[_selectedTopItems.length - 1]],
          selectedMonths: [_selectedMonths[_selectedTopItems.length - 1]],
        });
    } else {
      if (
        _.isEqual(
          selectedTopItems[0],
          _selectedTopItems[_selectedTopItems.length - 1],
        )
      ) {
        if (selectedTopItems.length === _selectedTopItems.length)
          deSelectAll();
        else
          handleFilter({
            selectedTopItems: _selectedTopItems,
            selectedMonths: _selectedMonths,
          });
      } else
        handleFilter({
          selectedTopItems: _selectedTopItems,
          selectedMonths: _selectedMonths,
        });
    }

    resetCanvas();
  };

  const onMouseMove = (event) => {
    if (!dragFlag || !canvasRef.current) return;

    const pos = getMousePositionOnCanvas(event);
    const newRX = Math.min(iX, pos.x);
    const newRY = Math.min(iY, pos.y);
    const newRW = iX ? Math.abs(iX - pos.x) : 0;
    const newRH = iY ? Math.abs(iY - pos.y) : 0;

    setX(pos.x);
    setRX(newRX);
    setRY(newRY);
    setRW(newRW);
    setRH(newRH);

    const context = canvasRef.current.getContext('2d');
    context.clearRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    );
    // drawRectangle will be called in useEffect when coordinates change
  };

  const onMouseOut = (event) => {
    if (iX && iY) {
      // drawRectangle(true);
    }
  };

  const endDraw = () => {
    if (iX && iY) {
      drawRectangle(true);
    }
  };

  const onSvgMouseDown = (event) => {
    if (event.button !== 0 || !canvasRef.current) return;

    const target = event.target;
    if (
      target &&
      (target.tagName === 'text' ||
        target.getAttribute('data-no-drag') === 'true')
    ) {
      return;
    }

    setIsMouseDown(true);

    if (dragFlag) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setCanvasIndex(0);
    setX(startX);
    setIX(startX);
    setIY(startY);
    setRX(startX);
    setRY(startY);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseMove = (event) => {
    if (!isMouseDown || dragFlag || event.buttons !== 1 || !canvasRef.current)
      return;

    const rect = canvasRef.current.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;

    setCanvasIndex(0);
    setX(startX);
    setIX(startX);
    setIY(startY);
    setRX(startX);
    setRY(startY);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseUp = () => {
    setIsMouseDown(false);
  };

  const onSvgMouseLeave = () => {
    setIsMouseDown(false);
  };

  const handleBar = useCallback((event, bar) => {
    if (dragFlag) return;

    const month = bar.bar.data.month;
    const year = bar.key;

    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      let _selectedBars = selectedBars.slice();
      let _selectedMonths = selectedMonths.slice();
      let _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
        if (month + 1 < defaultStartMonth)
          _selectedTopItems.push({ month: month, year: selectedYears[0] });
        else
          _selectedTopItems.push({ month: month, year: selectedYears[0] - 1 });
      } else {
        _selectedMonths.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }

      setSelectedBars(_selectedBars);
      handleFilter({
        selectedTopItems: _selectedTopItems,
        selectedMonths: _selectedMonths,
      });
    } else {
      let _selectedMonths = [];
      let exist = false;
      
      if (month + 1 < defaultStartMonth) {
        exist = selectedTopItems.find(item => item.month === month && item.year === year);
      } else {
        exist = selectedTopItems.find(item => item.month === month && item.year === year - 1);
      }

      if (exist) {
        setSelectedBars([]);
        handleFilter({
          selectedTopItems: [],
          selectedMonths: [],
        });
      } else {
        let _selectedTopItems, _selectedBars;
        if (month + 1 < defaultStartMonth)
          _selectedTopItems = [{ month: month, year: year }];
        else _selectedTopItems = [{ month: month, year: year - 1 }];
        _selectedBars = [event.target];
        _selectedMonths = [month];

        event.target.classList.add('barActive');
        
        setSelectedBars(_selectedBars);
        handleFilter({
          selectedTopItems: _selectedTopItems,
          selectedMonths: _selectedMonths,
        });
      }
    }
  }, [selectedMonths, selectedTopItems, selectedBars, selectedYears, defaultStartMonth, handleFilter, dragFlag]);

  const handleLabel = useCallback((event, month) => {
    let index = NaN;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      let _selectedBars = selectedBars.slice();
      let _selectedMonths = selectedMonths.slice();
      let _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
      } else {
        _selectedMonths.splice(index, 1);
      }

      setSelectedBars(_selectedBars);
      handleFilter({
        selectedMonths: _selectedMonths,
        selectedTopItems: _selectedTopItems,
      });
    } else {
      let _selectedBars = [];
      let _selectedTopItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      let _selectedMonths;
      if (!isNaN(index) && selectedMonths.length === 1) {
        _selectedMonths = [];
      } else {
        _selectedMonths = [month];
        if (month + 1 < defaultStartMonth)
          _selectedTopItems = [{ month: month, year: selectedYears[0] }];
        else _selectedTopItems = [{ month: month, year: selectedYears[0] - 1 }];
      }

      setSelectedBars(_selectedBars);
      handleFilter({
        selectedMonths: _selectedMonths,
        selectedTopItems: _selectedTopItems,
      });
    }
  }, [selectedMonths, selectedTopItems, selectedBars, selectedYears, defaultStartMonth, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('fpSelectedTopItems');
    handleFilter({
      selectedMonths: [],
      selectedTopItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, budget, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    let data = {},
      top,
      left;

    top = event.nativeEvent.layerY - 35;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 170
        : event.nativeEvent.layerX + 35;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      data = bar;
      data.budget = budget;
    } else {
      data['budget'] = budget;
    }

    data['isBar'] = isBar;
    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  // Component mount effect
  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [prepareData, onResize]);

  // Component update effect for summaryData changes
  useEffect(() => {
    prepareData();
  }, [summaryData, prepareData]);

  // Effect to redraw rectangle when coordinates change
  useEffect(() => {
    if (dragFlag && canvasRef.current && (rW > 0 || rH > 0)) {
      drawRectangle();
    }
  }, [rX, rY, rW, rH, dragFlag, drawRectangle]);

  const width = window.innerWidth;
  const height = 150;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );
  const offsetX = -2;
  const offsetWidth = -1.2;
  let minY = Math.min(Math.min(...totals), Math.min(...budgets));
  minY = Math.min(0, minY);
  let maxY = Math.max(Math.max(...totals), Math.max(...budgets));
  maxY = Math.max(0, maxY);
  
  // Ensure minY and maxY are valid numbers
  if (!isFinite(minY) || isNaN(minY)) minY = 0;
  if (!isFinite(maxY) || isNaN(maxY)) maxY = 100;

  // scales
  const xScale = scaleBand()
    .domain(data.map(getXValue))
    .rangeRound([0, xMax])
    .padding(0.2);

  const yScale = scaleLinear({
    domain: [minY * 1.1, maxY * 1.1],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let fiscalMonths = getMonths(defaultStartMonth);

  let futureLabelx, futureWidth, futureX;
  let pastLabelx, pastWidth;
  let futureCount = data.filter((d) => d.future === true).length;

  if (futureCount === 12) {
    futureLabelx = (width - xTransform - 8) / 2;
    futureWidth = width - xTransform - 8;
    futureX = 0;
  } else if (futureCount === 0) {
    pastLabelx = (width - xTransform - 8) / 2;
    pastWidth = width - xTransform - 8;
  } else {
    futureLabelx =
      xScale.step() * defaultMonth +
      (width - xScale.step() * defaultMonth) / 2 -
      xTransform / 2 -
      16;
    pastLabelx = (xScale.step() * defaultMonth) / 2;

    futureX =
      (xScale.paddingInner() * xScale.step()) / 2 +
      defaultMonth * xScale.step() -
      10;

    pastWidth =
      (xScale.paddingInner() * xScale.step()) / 2 +
      defaultMonth * xScale.step() +
      offsetX;
    futureWidth = width - xTransform - pastWidth - 8;
  }

  if (isNaN(futureLabelx)) futureLabelx = 0;
  if (isNaN(pastLabelx)) pastLabelx = 0;

  return (
    <div style={{
      height: '20vh',
    }} id="fp_top">
      <div
        className="subtitle"
        style={{ height: 33, display: 'flex', alignItems: 'center' }}
      >
        Projection vs Budget
      </div>

      <Grid container>
        <canvas
          width={width}
          height={height - 20}
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseUp={endDraw}
          onMouseOut={onMouseOut}
          style={{ position: 'fixed', zIndex: canvasIndex }}
        />
        <svg
          width={
            selectedYears[0] >= defaultYear ||
            selectedYears.indexOf(defaultYear) > -1
              ? defaultMonth !== 12
                ? width
                : width - 14
              : width - 14
          }
          height={height}
          onMouseDown={onSvgMouseDown}
          onMouseMove={onSvgMouseMove}
          onMouseUp={onSvgMouseUp}
          onMouseLeave={onSvgMouseLeave}
        >
          <Group top={margin.top} left={margin.left}>
            {futureCount > 0 ? (
              <Group>
                <Bar
                  width={futureWidth - 10}
                  height={height - 30}
                  x={futureX + 10}
                  y={0}
                  fill="#f0f0f0"
                  stroke="black"
                  strokeWidth={0}
                />
                <text
                  fill="black"
                  dx={futureLabelx}
                  dy={12}
                  fontSize="11px"
                  fontWeight="bold"
                >
                  Future
                </text>
              </Group>
            ) : null}
            {12 - futureCount > 0 ? (
              <Group>
                <text
                  fill="black"
                  dx={pastLabelx}
                  dy={12}
                  fontSize="11px"
                  fontWeight="bold"
                >
                  Past
                </text>
              </Group>
            ) : null}
            <rect
              x={0}
              y={0}
              width={width - xTransform - 15}
              height={height}
              fill="transparent"
              onMouseMove={(event) => {
                event = event || window.event;
                if ('buttons' in event) {
                  if (event.buttons === 1) {
                    setCanvasIndex(0);
                  }
                }
              }}
            />

            <BarStack
              data={data}
              keys={selectedYears}
              x={getXValue}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    // Skip rendering if bar has invalid coordinates
                    if (!isFinite(bar.x) || !isFinite(bar.y) || !isFinite(bar.height) || !isFinite(bar.width)) {
                      return null;
                    }
                    
                    // Ensure all values are valid numbers
                    const validX = Math.max(0, bar.x - 2);
                    const validY = Math.max(0, bar.y - 1);
                    const validHeight = Math.max(0, bar.height + 1);
                    const validWidth = Math.max(0, bar.width);
                    
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        x={validX}
                        y={validY}
                        height={validHeight}
                        width={validWidth}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={(event) => hideTooltipHandler()}
                        onMouseMove={(event) =>
                          showTooltipHandler(
                            event,
                            xScale,
                            bar,
                            budgets[bar.index],
                          )
                        }
                        onTouchEnd={(event) => hideTooltipHandler()}
                        onTouchMove={(event) =>
                          showTooltipHandler(
                            event,
                            xScale,
                            bar,
                            budgets[bar.index],
                          )
                        }
                      />
                    );
                  });
                });
              }}
            </BarStack>

            {budgets.map((forecast, index) => {
              let value = data[index].value[selectedYears[0]] ? forecast : 0;
              
              // Skip rendering if value is invalid
              if (!isFinite(value) || isNaN(value)) {
                return null;
              }
              
              const yPosition = yScale(value);
              
              // Skip rendering if yPosition is invalid
              if (!isFinite(yPosition) || isNaN(yPosition)) {
                return null;
              }
              
              const barWidth = (xMax - xScale.paddingInner() * xScale.step()) / fiscalMonths.length + offsetWidth;
              const barX = (xScale.paddingInner() * xScale.step()) / 2 + index * xScale.step() + offsetX;
              
              // Skip rendering if bar dimensions are invalid
              if (!isFinite(barWidth) || !isFinite(barX) || isNaN(barWidth) || isNaN(barX)) {
                return null;
              }
              
              return (
                <Group key={`bar-forecast-${index}`}>
                  <Bar
                    width={Math.max(0, barWidth)}
                    height={1}
                    x={Math.max(0, barX)}
                    y={Math.max(0, yPosition)}
                    fill="black"
                    stroke="black"
                    strokeWidth={1}
                    onMouseLeave={(event) => hideTooltipHandler()}
                    onMouseMove={(event) =>
                      showTooltipHandler(event, xScale, value, value, false)
                    }
                    onTouchEnd={(event) => hideTooltipHandler()}
                    onTouchMove={(event) =>
                      showTooltipHandler(event, xScale, value, value, false)
                    }
                  />
                </Group>
              );
            })}

            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />

            <AxisBottom
              hideTicks={true}
              scale={xScale}
              top={yMax}
              stroke="black"
              tickStroke="black"
              tickLabelProps={(value, index) => ({
                fontSize: 11,
                textAnchor: 'middle',
                dy: '-0.5em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  fill={
                    selectedMonths.indexOf(parseInt(formattedValue)) > -1
                      ? activeLabelColor
                      : 'black'
                  }
                  style={{
                    fontWeight:
                      selectedMonths.indexOf(parseInt(formattedValue)) > -1 ? 700 : 500,
                  }}
                  onClick={(event) =>
                    handleLabel(event, parseInt(formattedValue))
                  }
                >
                  {enMonths[formattedValue]}
                </text>
              )}
            />
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + pageYOffset}
            left={tooltipLeft}
          >
            {tooltipData.isBar ? (
              <div className="bkg-white color-black">
                <div className="pdv-10">
                  <strong>
                    FY {tooltipData.key} : {enMonths[getXValue(tooltipData.bar.data)]}
                  </strong>
                </div>
                {tooltipData.bar.data.future ? (
                  <div className="ft-12">
                    Forecast:{' '}
                    <strong>
                      $
                      {thousandFormat(
                        tooltipData.bar.data.value[tooltipData.key]
                          .ForecastFees,
                      )}
                    </strong>
                  </div>
                ) : (
                  <div className="ft-12">
                    Actual:{' '}
                    <strong>
                      $
                      {thousandFormat(
                        tooltipData.bar.data.value[tooltipData.key]
                          ?.ActualFees,
                      )}
                    </strong>
                  </div>
                )}
                {tooltipData.bar.data.future ? (
                  <div className="ft-12">
                    Budget:{' '}
                    <strong>
                      $
                      {thousandFormat(
                        tooltipData.bar.data.value[tooltipData.key]
                          .BudgetFees,
                      )}
                    </strong>
                  </div>
                ) : (
                  <div className="ft-12">
                    Budget:{' '}
                    <strong>${thousandFormat(tooltipData.budget)}</strong>
                  </div>
                )}
              </div>
            ) : (
              <div className="bkg-white color-black">
                Sum IncomeMTD Budget = {thousandFormat(tooltipData.budget)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultYear: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
